import { useCallback } from 'react';
import { API_URL } from '../../../utils/api';

/**
 * Custom hook för att hantera uppföljning av estimat
 * Inkluderar logik för tidsförslag och direkt bokning av uppföljningsmöten
 */
const useEstimatFollowup = ({
  currentEstimatId,
  procurement,
  customer,
  lead,
  onSuccess,
  onError,
  setBookedFollowupSlot,
}) => {
  
  /**
   * Skickar tidsförslag till kunden
   * @param {Array} proposals - Lista med tidsförslag
   * @param {string} message - Meddelande till kunden
   */
  const handleSuggestTime = useCallback(async (proposals, message) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!currentEstimatId) {
        throw new Error('Inget estimat-ID tillgängligt. Spara estimatet först.');
      }
      
      // Skicka tidsförslag till backend API
      const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/time-proposals`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          proposals: proposals,
          message: message || ''
        })
      });
      
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Kunde inte skicka tidsförslag');
      }
      
      // Callback för framgång
      if (onSuccess) {
        onSuccess({
          title: 'Tidsförslag skickade', 
          message: `${data.proposals.length} tidsförslag har skickats till kunden via e-post. Kunden kan acceptera ett förslag på estimatsidan.`, 
          type: 'success',
          shouldRefresh: true
        });
      }
      
    } catch (err) {
      console.error('❌ Fel vid skickande av tidsförslag:', err);
      
      // Callback för fel
      if (onError) {
        onError({ 
          title: 'Kunde inte skicka förslag', 
          message: err.message || 'Okänt fel', 
          type: 'error' 
        });
      }
      
      throw err; // Re-throw så att modalen vet att något gick fel
    }
  }, [currentEstimatId, onSuccess, onError]);
  
  /**
   * Bokar ett uppföljningsmöte direkt i kalendern
   * @param {string} dateStr - Datum för mötet (YYYY-MM-DD)
   * @param {string} time - Tid för mötet (HH:mm)
   * @param {string} displaySlot - Visningstext för tidssloten
   */
  const handleBookMeeting = useCallback(async (dateStr, time, displaySlot, durationMinutes) => {
    try {
      const token = localStorage.getItem('token');
      
      // Hämta customerId från Procurement (primärt) eller fallback
      let customerIdToUse = null;
      if (procurement?.customerId) {
        customerIdToUse = typeof procurement.customerId === 'string' 
          ? procurement.customerId 
          : procurement.customerId._id || procurement.customerId.id;
      }
      
      // Fallback om ingen procurement
      if (!customerIdToUse) {
        customerIdToUse = customer?._id || lead?.convertedCustomerId || lead?.customerId || null;
      }
      
      if (!customerIdToUse) {
        throw new Error('Kunde inte hitta kund-ID.');
      }
      
      // Använd samma API som när man bokar från kundens förslagna tider
      const response = await fetch(`${API_URL}/api/estimat/customer/${customerIdToUse}/followup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: dateStr,
          time,
          displaySlot: displaySlot,
          durationMinutes
        })
      });
      
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Bokning misslyckades');
      }
      
      // Uppdatera state för att visa det bokade mötet
      if (setBookedFollowupSlot) {
        setBookedFollowupSlot(displaySlot);
      }
      
      // Callback för framgång
      if (onSuccess) {
        onSuccess({
          title: 'Möte bokat', 
          message: 'Uppföljningsmötet har bokats och kunden kommer få en kallelse.', 
          type: 'success',
          shouldRefresh: true
        });
      }
      
    } catch (err) {
      console.error('❌ Fel vid bokning av möte:', err);
      
      // Callback för fel
      if (onError) {
        onError({ 
          title: 'Kunde inte boka', 
          message: err.message || 'Okänt fel', 
          type: 'error' 
        });
      }
      
      throw err; // Re-throw så att modalen vet att något gick fel
    }
  }, [procurement, customer, lead, setBookedFollowupSlot, onSuccess, onError]);
  
  return {
    handleSuggestTime,
    handleBookMeeting,
  };
};

export default useEstimatFollowup;
